import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TextInputModern, Button, Badge } from '@pavanhiro/common-ui';
import { Lock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    feedback: string;
    color: 'danger' | 'warning' | 'success';
  }>({ score: 0, feedback: '', color: 'danger' });

  // Validate token on mount
  useEffect(() => {
    validateResetToken();
  }, [token]);

  // Update password strength as user types
  useEffect(() => {
    if (password) {
      const strength = calculatePasswordStrength(password);
      setPasswordStrength(strength);
    }
  }, [password]);

  const validateResetToken = async () => {
    if (!token) {
      setError('Invalid or missing reset token');
      setTokenValid(false);
      setValidatingToken(false);
      return;
    }

    try {
      const response = await fetch(`/api/v1/auth/validate-reset-token?token=${token}`);
      const data = await response.json();

      if (response.ok && data.valid) {
        setTokenValid(true);
      } else {
        setError(data.error || 'Invalid or expired reset token');
        setTokenValid(false);
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setError('Failed to validate reset token. Please try again.');
      setTokenValid(false);
    } finally {
      setValidatingToken(false);
    }
  };

  const calculatePasswordStrength = (pwd: string): {
    score: number;
    feedback: string;
    color: 'danger' | 'warning' | 'success';
  } => {
    let score = 0;
    let feedback = '';
    let color: 'danger' | 'warning' | 'success' = 'danger';

    // Length check
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(pwd)) score += 1; // lowercase
    if (/[A-Z]/.test(pwd)) score += 1; // uppercase
    if (/[0-9]/.test(pwd)) score += 1; // numbers
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1; // special chars

    // Determine feedback
    if (score <= 2) {
      feedback = 'Weak password - Add uppercase, numbers, and symbols';
      color = 'danger';
    } else if (score <= 4) {
      feedback = 'Fair password - Consider adding more variety';
      color = 'warning';
    } else {
      feedback = 'Strong password ✓';
      color = 'success';
    }

    return { score, feedback, color };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (passwordStrength.score < 3) {
      setError('Password is too weak. Please use a stronger password.');
      return;
    }

    // Validate minimum requirements
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(password)) {
      setError('Password must contain uppercase, lowercase, and numbers');
      return;
    }

    setLoading(true);

    try {
      // Call backend API to reset password
      const response = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password. Please try again.');
      }

      // Show success message
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <div className="logo-icon">🔐</div>
              <h1 className="logo-text">ZoAuth Auth</h1>
            </div>
            <h2 className="auth-title">Create New Password</h2>
            <p className="auth-subtitle">
              {success
                ? 'Your password has been successfully reset'
                : 'Enter your new password below'}
            </p>
          </div>

          {/* Validating Token State */}
          {validatingToken ? (
            <div className="validating-state">
              <div className="spinner"></div>
              <p>Validating reset link...</p>
            </div>
          ) : !tokenValid ? (
            /* Invalid Token State */
            <div className="error-state">
              <div className="error-icon-wrapper">
                <XCircle size={64} className="error-icon" />
              </div>
              <h3 className="error-title">Invalid Reset Link</h3>
              <Badge themeColor="danger" variant="soft" className="error-badge">
                {error}
              </Badge>
              <div className="error-instructions">
                <p>❌ This reset link is invalid or has expired</p>
                <p>⏰ Reset links expire after 1 hour</p>
                <p>🔄 Request a new password reset link</p>
              </div>
              <div className="error-actions">
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => navigate('/forgot-password')}
                >
                  Request New Reset Link
                </Button>
                <Button
                  variant="outline"
                  size="large"
                  onClick={() => navigate('/login')}
                  className="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          ) : success ? (
            /* Success State */
            <div className="success-state">
              <div className="success-icon-wrapper">
                <CheckCircle2 size={64} className="success-icon" />
              </div>
              <h3 className="success-title">Password Reset Successful!</h3>
              <p className="success-message">
                Your password has been successfully updated. You can now log in with your
                new password.
              </p>
              <Badge themeColor="success" variant="soft" className="success-badge">
                Redirecting to login page in 3 seconds...
              </Badge>
              <div className="success-actions">
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </Button>
              </div>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="auth-form">
              {/* Error Message */}
              {error && (
                <Badge themeColor="danger" variant="soft" className="auth-error-badge">
                  {error}
                </Badge>
              )}

              {/* New Password Field */}
              <div className="form-group">
                <TextInputModern
                  variant="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(value) => setPassword(value)}
                  required
                  size="lg"
                  styleType="outline"
                  passwordToggle={true}
                  leftSection={<Lock size={20} />}
                  autoComplete="new-password"
                  disabled={loading}
                />
                {/* Password Strength Indicator */}
                {password && (
                  <div className="password-strength">
                    <div className="password-strength-bar">
                      <div
                        className={`password-strength-fill strength-${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                      ></div>
                    </div>
                    <p className={`password-strength-text text-${passwordStrength.color}`}>
                      {passwordStrength.feedback}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <TextInputModern
                variant="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(value) => setConfirmPassword(value)}
                required
                size="lg"
                styleType="outline"
                passwordToggle={true}
                leftSection={<Lock size={20} />}
                autoComplete="new-password"
                disabled={loading}
              />

              {/* Password Requirements */}
              <div className="password-requirements">
                <p className="requirements-title">
                  <AlertTriangle size={16} /> Password Requirements:
                </p>
                <ul className="requirements-list">
                  <li className={password.length >= 8 ? 'requirement-met' : ''}>
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(password) ? 'requirement-met' : ''}>
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(password) ? 'requirement-met' : ''}>
                    One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(password) ? 'requirement-met' : ''}>
                    One number
                  </li>
                  <li className={/[^a-zA-Z0-9]/.test(password) ? 'requirement-met' : ''}>
                    One special character (recommended)
                  </li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading}
                className="auth-submit-btn"
                disabled={
                  !password || !confirmPassword || password !== confirmPassword
                }
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              {/* Back to Login Link */}
              <div className="auth-footer-links">
                <Link to="/login" className="auth-link">
                  Cancel and Return to Login
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="auth-page-footer">
          <p>
            Need help? <a href="mailto:support@zoauth.com">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
